import React from "react";
import { Formik } from "formik";
import { formatPhoneNumber } from "@eave/shared/utils/phone";
import {
  Button,
  ButtonSet,
  Checkbox,
  DatePicker,
  DatePickerInput,
  Form,
  FormGroup,
  Select,
  SelectItem,
  TextInput,
} from "carbon-components-react";
import stateOptions from "web_modules/forms/data/stateOptions";
import { EditingState } from "web_modules/shared/BaseTable";
import { makeDatePickerEvent, makeEvent } from "web_modules/shared/FormHelpers/carbon-helpers";
import { EmploymentType, IncomeType } from "underwriter_dashboard/shared/types";
import { validationSchema } from "../form-values";
import { LoanApplicationBorrowers } from "@eave/shared/types/api";
import { getBorrowerEntityAsString } from "@eave/shared/utils/borrowers";

interface Props {
  formState: any;
  onSubmit: any;
  borrowers: LoanApplicationBorrowers;
  editingMode: EditingState;
  onRequestClose: Function;
}

const EmploymentModalForm: React.FC<Props> = ({
  formState,
  onSubmit,
  borrowers,
  editingMode,
  onRequestClose,
}: Props) => {
  return (
    <Formik
      enableReinitialize
      onSubmit={onSubmit}
      initialValues={formState}
      validationSchema={validationSchema}
    >
      {({ values, errors, touched, handleChange, handleBlur, setValues, handleSubmit }) => {
        const {
          name,
          ein,
          borrower_entity,
          start_date,
          end_date,
          employer_is_party_to_transaction,
          address_line1,
          address_line2,
          city,
          postal_code,
          state,
          contact,
          contact_extension,
          income_type,
          self_employed_ownership_percent,
          shows_on_tax_returns,
          title,
          employment_type,
          is_seasonal_income,
          is_foreign_income,
          years_in_line_of_work,
        } = values;
        return (
          <Form
            className="employment-modal-form"
            onSubmit={e => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <FormGroup legendText="">
              <TextInput
                id="employer-name"
                name="name"
                size="sm"
                value={name}
                labelText="Employer Name *"
                placeholder="HomeLight, Inc"
                type="text"
                invalid={!!errors.name && touched.name}
                invalidText={errors.name}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <div style={{ marginBottom: "10px" }} />
              <TextInput
                size="sm"
                id="ein"
                name="ein"
                value={ein}
                labelText="EIN (optional)"
                placeholder="123456789"
                type="text"
                invalid={errors.ein && touched.ein}
                invalidText={errors.ein}
                onChange={event => {
                  const e = makeEvent("ein", event.target.value);
                  handleChange(e);
                }}
                onBlur={handleBlur}
              />
              <div style={{ marginBottom: "10px" }} />
              <Select
                id="borrower-name"
                name="borrower_entity"
                value={borrower_entity}
                invalid={!!errors.borrower_entity && touched.borrower_entity}
                invalidText={errors.borrower_entity}
                labelText="Borrower entity *"
                onBlur={handleBlur}
                onChange={e => {
                  const event = makeEvent("borrower_entity", parseInt(e.target.value, 10));
                  handleChange(event);
                }}
                disabled={editingMode === EditingState.UPDATE}
                size="sm"
              >
                <SelectItem text="Select a borrower" value={0} />
                {borrowers &&
                  Object.keys(borrowers).map(be => (
                    <SelectItem
                      key={be}
                      value={Number(be)}
                      text={getBorrowerEntityAsString(Number(be))}
                    />
                  ))}
              </Select>
            </FormGroup>
            <FormGroup legendText="Start & End Date">
              <DatePicker
                id="date-picker-start-date"
                name="start_date"
                datePickerType="single"
                dateFormat="Y-m-d"
                value={start_date}
                onChange={event => handleChange(makeDatePickerEvent("start_date", event))}
                onBlur={handleBlur}
              >
                <DatePickerInput
                  id="date-picker-start-date-input-id"
                  name="start_date"
                  iconDescription="Select a start date"
                  labelText="Start Date *"
                  placeholder="yyyy-mm-dd"
                  type="text"
                  size="sm"
                  pattern={null}
                  invalid={errors.start_date && touched.start_date}
                />
              </DatePicker>
              <div style={{ marginBottom: "10px" }} />
              <DatePicker
                id="date-picker-end-date"
                name="end_date"
                dateFormat="Y-m-d"
                datePickerType="single"
                value={end_date}
                onChange={event => handleChange(makeDatePickerEvent("end_date", event))}
                onBlur={handleBlur}
              >
                <DatePickerInput
                  id="date-picker-end-date-input-id"
                  name="end_date"
                  iconDescription="Select a end date. (If current, leave blank)"
                  labelText="End Date"
                  pattern={null}
                  placeholder="yyyy-mm-dd"
                  type="text"
                  size="sm"
                  invalid={errors.end_date && touched.end_date}
                  invalidText={errors.end_date}
                />
              </DatePicker>
              <TextInput
                size="sm"
                id="years_in_line_of_work"
                name="years_in_line_of_work"
                value={years_in_line_of_work}
                labelText="Years in line of work"
                placeholder="Enter years"
                type="text"
                invalid={errors.years_in_line_of_work && touched.years_in_line_of_work}
                invalidText={errors.years_in_line_of_work}
                onChange={event => {
                  const v = parseInt(event.target.value, 10);
                  const e = makeEvent("years_in_line_of_work", v);
                  handleChange(e);
                }}
                onBlur={handleBlur}
              />
              <div style={{ marginBottom: "10px" }} />
              <Checkbox
                id="employer-is-party-to-transaction"
                name="employer_is_party_to_transaction"
                labelText="Employer is a family member, property seller, real estate agent, or other party to the transaction"
                checked={employer_is_party_to_transaction}
                onChange={() => {
                  const e = makeEvent(
                    "employer_is_party_to_transaction",
                    !employer_is_party_to_transaction,
                  );
                  handleChange(e);
                }}
              />
            </FormGroup>
            <FormGroup legendText="Employer Address">
              <TextInput
                id="address_line1"
                name="address_line1"
                value={address_line1}
                labelText="Address Line 1 *"
                placeholder="1 Infinite Loop"
                type="text"
                invalid={errors.address_line1 && touched.address_line1}
                invalidText="Enter a valid address"
                onChange={handleChange}
                onBlur={handleBlur}
                size="sm"
              />
              <div style={{ marginBottom: "10px" }} />
              <TextInput
                id="address_line2"
                name="address_line2"
                value={address_line2}
                labelText="Address Line 2"
                placeholder="Floor 2"
                type="text"
                invalid={errors.address_line2 && touched.address_line2}
                invalidText="Enter a valid address line 2"
                onChange={handleChange}
                onBlur={handleBlur}
                size="sm"
              />
              <div style={{ marginBottom: "10px" }} />
              <TextInput
                id="city"
                name="city"
                value={city}
                labelText="City *"
                placeholder="Cupertino"
                type="text"
                invalid={errors.city && touched.city}
                invalidText="Enter a valid city"
                onChange={handleChange}
                onBlur={handleBlur}
                size="sm"
              />
              <div style={{ marginBottom: "10px" }} />
              <Select
                id="state"
                name="state"
                value={state}
                labelText="State *"
                invalid={errors.state && touched.state}
                invalidText="Enter a valid state"
                onChange={handleChange}
                onBlur={handleBlur}
                size="sm"
              >
                <SelectItem text="Select State" value="" />
                {stateOptions.map(stateOption => (
                  <SelectItem
                    key={stateOption.label}
                    text={stateOption.label}
                    value={stateOption.value}
                  />
                ))}
              </Select>
              <div style={{ marginBottom: "10px" }} />
              <TextInput
                id="postal_code"
                name="postal_code"
                value={postal_code}
                invalid={errors.postal_code && touched.postal_code}
                invalidText={errors.postal_code}
                labelText="Postal Code *"
                placeholder="90210"
                type="text"
                onChange={handleChange}
                onBlur={handleBlur}
                size="sm"
              />
              <div style={{ marginBottom: "10px" }} />
            </FormGroup>
            <FormGroup legendText="Contact Information">
              <TextInput
                id="contact"
                name="contact"
                value={contact && formatPhoneNumber(contact)}
                labelText="Phone Number *"
                placeholder="555-555-5555"
                type="text"
                invalid={errors.contact && touched.contact}
                invalidText="Enter a valid phone number"
                onChange={handleChange}
                onBlur={handleBlur}
                size="sm"
              />
              <div style={{ marginBottom: "10px" }} />
              <TextInput
                id="contact_extension"
                name="contact_extension"
                value={contact_extension}
                labelText="Phone Number Extension"
                placeholder="123"
                type="text"
                invalid={errors.contact_extension && touched.contact_extension}
                invalidText="Enter a valid phone number extension"
                onChange={handleChange}
                onBlur={handleBlur}
                size="sm"
              />
            </FormGroup>
            <FormGroup legendText="Employee Information">
              <TextInput
                id="title"
                name="title"
                value={title}
                invalid={errors.title && touched.title}
                invalidText="Enter employee title"
                labelText="Employee title *"
                placeholder="Librarian"
                type="text"
                onChange={handleChange}
                onBlur={handleBlur}
                size="sm"
              />
              <div style={{ marginBottom: "10px" }} />
              <Select
                id="employment_type"
                name="employment_type"
                value={employment_type}
                labelText="Employment type *"
                style={{ marginBottom: "10px" }}
                invalid={errors.employment_type && touched.employment_type}
                invalidText="Enter a valid employment type"
                onChange={handleChange}
                onBlur={handleBlur}
                size="sm"
              >
                <SelectItem text="Select Employment Type" value="" />
                {[
                  {
                    label: "Full Time",
                    value: EmploymentType.FullTime,
                  },
                  {
                    label: "Part Time",
                    value: EmploymentType.PartTime,
                  },
                ].map(o => (
                  <SelectItem key={o.label} text={o.label} value={o.value} />
                ))}
              </Select>
              <div style={{ marginBottom: "10px" }} />
              <Checkbox
                id="is_foreign_income"
                name="is_foreign_income"
                labelText="Is This Foreign Income?"
                checked={is_foreign_income}
                onChange={() => {
                  const e = makeEvent("is_foreign_income", !is_foreign_income);
                  handleChange(e);
                }}
              />
              <div style={{ marginBottom: "10px" }} />
              <Checkbox
                id="is_seasonal_income"
                name="is_seasonal_income"
                labelText="Is This Seasonal Income?"
                checked={is_seasonal_income}
                onChange={() => {
                  const e = makeEvent("is_seasonal_income", !is_seasonal_income);
                  handleChange(e);
                }}
              />
              <div style={{ marginBottom: "10px" }} />
              <Select
                id="income_type"
                name="income_type"
                value={income_type}
                labelText="Income type *"
                style={{ marginBottom: "10px" }}
                invalid={errors.income_type && touched.income_type}
                invalidText="Enter a valid income type"
                onChange={event => {
                  if (event.target.value !== IncomeType.SelfEmployed) {
                    setValues({
                      ...values,
                      income_type: event.target.value,
                      self_employed_ownership_percent: "",
                      shows_on_tax_returns: null,
                    } as any);
                  } else {
                    // If income_type is self-employed, update the form as usual
                    setValues({
                      ...values,
                      income_type: event.target.value,
                    } as any);
                  }
                }}
                size="sm"
              >
                <SelectItem text="Select Income Type" value="" />
                {[
                  {
                    label: "Self Employed",
                    value: IncomeType.SelfEmployed,
                  },
                  {
                    label: "Salaried",
                    value: IncomeType.Salaried,
                  },
                ].map(o => (
                  <SelectItem key={o.label} text={o.label} value={o.value} />
                ))}
              </Select>
              <div style={{ marginBottom: "10px" }} />
              {income_type === IncomeType.SelfEmployed && (
                <>
                  <TextInput
                    id="self_employed_ownership_percent"
                    name="self_employed_ownership_percent"
                    value={self_employed_ownership_percent}
                    labelText="Self Employed Ownership Percent * (omit the % symbol)"
                    placeholder="100"
                    type="text"
                    style={{ marginBottom: "10px" }}
                    invalid={
                      errors.self_employed_ownership_percent &&
                      touched.self_employed_ownership_percent
                    }
                    invalidText="Enter a valid percentage, an integer from 0-100."
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size="sm"
                  />
                  <div style={{ marginBottom: "10px" }} />
                  <Select
                    id="shows_on_tax_returns"
                    name="shows_on_tax_returns"
                    value={shows_on_tax_returns}
                    labelText="Shows on tax returns *"
                    invalid={errors.shows_on_tax_returns && touched.shows_on_tax_returns}
                    invalidText="Enter a valid value"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size="sm"
                  >
                    {[
                      {
                        label: "Shows on tax returns",
                        value: "",
                      },
                      {
                        label: "Yes",
                        value: true,
                      },
                      {
                        label: "No",
                        value: false,
                      },
                    ].map(o => (
                      <SelectItem key={o.label} text={o.label} value={o.value} />
                    ))}
                  </Select>
                </>
              )}
            </FormGroup>
            <ButtonSet>
              <Button
                kind="secondary"
                size="lg"
                onClick={() => onRequestClose()}
                style={{ maxWidth: "50%" }}
              >
                Cancel
              </Button>
              <Button kind="primary" size="lg" type="submit" style={{ maxWidth: "50%" }}>
                {editingMode === EditingState.CREATE ? "Save" : "Update"}
              </Button>
            </ButtonSet>
          </Form>
        );
      }}
    </Formik>
  );
};

export default EmploymentModalForm;
